import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import Seo from "../components/Seo";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from "../components/ui/alert-dialog";
import { api, WS_URL } from "../lib/api";
import { registerPush } from "../lib/pushService";
import Navbar from "../components/Navbar";
import MapView from "../components/MapView";
import LanguageDialog from "../components/LanguageDialog";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { MapPin, Calendar, User, ArrowRight, Loader2, Star, Eye, MessageCircle, ChevronLeft, Bell, Globe, CreditCard, HelpCircle, LogOut, ChevronRight, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import RateBookingDialog from "../components/RateBookingDialog";
import BookingDetailsDialog from "../components/BookingDetailsDialog";
import useAutoRefresh from "../lib/useAutoRefresh";

const LOCATION_KEY = "user_coords";

const statusColor = {
  pending: "bg-[#F59E0B]/15 text-[#B45309]",
  accepted: "bg-[#1C4532]/15 text-[#1C4532]",
  on_the_way: "bg-[#D9734E]/15 text-[#C26240]",
  completed: "bg-[#22C55E]/15 text-[#15803D]",
  rejected: "bg-[#94A3B8]/15 text-[#475569]",
  cancelled: "bg-[#94A3B8]/15 text-[#475569]",
};

export default function UserDashboard() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [services, setServices] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [coords, setCoords] = useState(null);
  const [loadingLoc, setLoadingLoc] = useState(false);
  const [tab, setTab] = useState("profile");
  const [logoutOpen, setLogoutOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const [langOpen, setLangOpen] = useState(false);
  const watchRef = useRef(null);

  // Profile edit
  const [editing, setEditing] = useState(false);
  const [profile, setProfile] = useState({ name: "", phone: "", address: "" });

  // Rating dialog
  const [rateBooking, setRateBooking] = useState(null);
  const [rateOpen, setRateOpen] = useState(false);

  // Booking details dialog (live track + chat)
  const [detailsId, setDetailsId] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const openDetails = (id) => { setDetailsId(id); setDetailsOpen(true); };

  // Restore saved location on mount & auto-fetch nearby pros
  useEffect(() => {
    const saved = localStorage.getItem(LOCATION_KEY);
    if (saved) {
      try {
        const c = JSON.parse(saved);
        setCoords(c);
        api.get(`/workers/nearby?lat=${c.lat}&lng=${c.lng}`)
          .then(r => setWorkers(r.data))
          .catch(() => {});
      } catch {}
    }
  }, []);

  // Clean up watch on unmount
  useEffect(() => {
    return () => {
      if (watchRef.current !== null) {
        navigator.geolocation.clearWatch(watchRef.current);
      }
    };
  }, []);

  // Register for push notifications on mount
  useEffect(() => {
    if (user) registerPush();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (!user) return;
    setProfile({ name: user.name || "", phone: user.phone || "", address: user.address || "" });
    loadAll();

    function playChime() {
      try { const a = new Audio("/notification.mp3"); a.volume = 0.5; a.play().catch(() => {}); } catch {}
    }
    function pushNotify(title, body) {
      if (!("Notification" in window)) return;
      if (Notification.permission === "granted") {
        try { new Notification(title, { body, tag: "booking-update" }); } catch {}
      } else if (Notification.permission !== "denied") {
        Notification.requestPermission().then(perm => {
          if (perm === "granted") { try { new Notification(title, { body, tag: "booking-update" }); } catch {} }
        });
      }
    }

    const ws = new WebSocket(`${WS_URL}/api/ws/${user.id}`);
    ws.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data);
        if (data.type === "booking_update") {
          const b = data.booking;
          const status = b.status;
          if (status === "accepted") {
            playChime();
            pushNotify("Worker Assigned!", `${b.service_name} - ${b.address || ""}`);
            toast.success(`${b.service_name} confirmed! A pro is on the way.`, { duration: 8000 });
          } else if (status === "on_the_way") {
            playChime();
            pushNotify("Pro is on the way!", `${b.service_name} - ${b.address || ""}`);
            toast.info("Your pro is on the way!", { duration: 6000 });
          } else if (status === "completed") {
            pushNotify("Service completed!", b.service_name);
            toast.success("Service completed! Please rate your experience.", { duration: 6000 });
          } else if (status === "cancelled") {
            pushNotify("Booking cancelled", b.service_name);
            toast.error("Booking was cancelled.");
          } else {
            toast.success(`Booking ${status.replace(/_/g, " ")}`);
          }
          setTab("bookings");
          loadAll();
        }
      } catch {}
    };
    return () => ws.close();
    // eslint-disable-next-line
  }, [user]);

  const loadAll = async () => {
    const [b, s] = await Promise.all([
      api.get("/bookings/mine"),
      api.get("/services"),
    ]);
    setBookings(b.data);
    setServices(s.data);
  };

  // Auto-refresh bookings + services every 8s while tab is visible
  useAutoRefresh(() => { if (user) loadAll(); }, [user?.id], 8000);

  const detectLocation = () => {
    setLoadingLoc(true);
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); setLoadingLoc(false); return; }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const c = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setCoords(c);
        localStorage.setItem(LOCATION_KEY, JSON.stringify(c));
        // Start watching position for continuous live updates
        if (watchRef.current !== null) navigator.geolocation.clearWatch(watchRef.current);
        watchRef.current = navigator.geolocation.watchPosition(
          async (newPos) => {
            const nc = { lat: newPos.coords.latitude, lng: newPos.coords.longitude };
            setCoords(nc);
            localStorage.setItem(LOCATION_KEY, JSON.stringify(nc));
            try { setWorkers((await api.get(`/workers/nearby?lat=${nc.lat}&lng=${nc.lng}`)).data); } catch {}
          },
          () => {},
          { enableHighAccuracy: true }
        );
        try {
          const r = await api.get(`/workers/nearby?lat=${c.lat}&lng=${c.lng}`);
          setWorkers(r.data);
          toast.success(`${r.data.length} pros online nearby`);
        } catch (e) { toast.error("Couldn't fetch nearby pros"); }
        setLoadingLoc(false);
      },
      () => { toast.error("Location denied"); setLoadingLoc(false); }
    );
  };

  const saveProfile = async () => {
    try {
      await api.put("/users/me", profile);
      await refresh();
      toast.success("Profile updated");
      setEditing(false);
    } catch { toast.error("Update failed"); }
  };

  if (!user) return null;

  return (
    <><Seo title="My Dashboard" description="Manage your bookings, profile, and service history on Universal Tree." noindex />
    <div className="min-h-screen flex flex-col dashboard-bg">
      <Navbar />
      <main className="flex-1 max-w-screen-2xl mx-auto px-4 sm:px-6 py-6 md:py-8 w-full">
        <div className="grid grid-cols-12 gap-4 lg:gap-6">
          <div className="premium-surface col-span-12 md:rounded-sm rounded-xl p-5 md:p-8 hidden md:block">
            <p className="text-xs uppercase tracking-[0.25em] text-[#D9734E] font-bold">{t("userDashboard.badge")}</p>
            <h1 className="font-display text-4xl lg:text-5xl font-light tracking-tight mt-2">{t("userDashboard.greeting")}, {user.name.split(" ")[0]}.</h1>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-[#646964]">{t("userDashboard.description")}</p>
          </div>

          <Tabs value={tab} onValueChange={setTab} className="col-span-12">
            <div className="overflow-x-auto -mx-4 px-4 scrollbar-hide md:block hidden">
              <TabsList className="bg-[#F3F1ED] md:rounded-sm rounded-lg p-1 h-12 w-full sm:w-auto inline-flex gap-1">
                <TabsTrigger value="bookings" data-testid="tab-bookings" className="md:rounded-sm rounded-lg data-[state=active]:bg-[#1C4532] data-[state=active]:text-white data-[state=active]:shadow-sm px-5 whitespace-nowrap text-sm font-semibold transition-all">{t("userDashboard.tab.bookings")}</TabsTrigger>
                <TabsTrigger value="nearby" data-testid="tab-nearby" className="md:rounded-sm rounded-lg data-[state=active]:bg-[#1C4532] data-[state=active]:text-white data-[state=active]:shadow-sm px-5 whitespace-nowrap text-sm font-semibold transition-all">{t("userDashboard.tab.nearby")}</TabsTrigger>
                <TabsTrigger value="profile" data-testid="tab-profile" className="md:rounded-sm rounded-lg data-[state=active]:bg-[#1C4532] data-[state=active]:text-white data-[state=active]:shadow-sm px-5 whitespace-nowrap text-sm font-semibold transition-all">{t("userDashboard.tab.profile")}</TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="bookings" className="mt-5 md:mt-6">
              <button onClick={() => setTab("profile")} className="flex items-center gap-1.5 text-sm text-[#646964] mb-3 md:hidden active:text-[#1A1D1A]">
                <ChevronLeft size={16} /> {t("userDashboard.backToProfile")}
              </button>
              <div className="mobile-stack grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                {bookings.map((b) => (
                  <article key={b.id} data-testid={`booking-${b.id}`} onClick={() => openDetails(b.id)} className="mobile-card haptic-tap p-4 md:p-6 md:rounded-sm rounded-xl cursor-pointer transition-all hover:-translate-y-1 hover:border-[#1C4532]">
                    <div className="flex justify-between items-start gap-4">
                      <div className="min-w-0 flex-1">
                        <p className="font-display text-base md:text-lg text-[#1A1D1A] truncate">{b.service_name}</p>
                        <p className="text-xs text-[#646964] mt-1 font-mono">#{b.id}</p>
                      </div>
                      <Badge className={`${statusColor[b.status] || ""} md:rounded-sm rounded-full font-medium shrink-0 ${b.status === "pending" ? "animate-pulse" : ""}`}>
                        {b.status === "pending" && !b.worker_id
                          ? (b.worker_queue && JSON.parse(b.worker_queue).length > 0
                              ? `📞 Contacting ${(b.queue_index || 0) + 1} of ${JSON.parse(b.worker_queue).length}`
                              : "🔍 " + (t("userDashboard.searching") || "Searching..."))
                          : b.status.replace(/_/g, " ")}
                      </Badge>
                    </div>
                    <div className="mt-3 md:mt-4 space-y-1.5 text-sm text-[#646964]">
                      <p className="flex items-start gap-2"><MapPin size={14} className="mt-0.5 shrink-0"/><span className="line-clamp-1">{b.address}</span></p>
                      <p className="flex items-center gap-2"><Calendar size={14} className="shrink-0"/>{new Date(b.created_at).toLocaleString()}</p>
                    </div>
                    <div className="mt-3 md:mt-4 pt-3 md:pt-4 border-t border-[#E2E0D9] flex items-center justify-between" onClick={(e) => e.stopPropagation()}>
                      <span className="font-display text-lg md:text-xl text-[#1C4532]">₹{b.amount}</span>
                      <div className="flex items-center gap-2">
                        {["accepted", "on_the_way"].includes(b.status) && (
                          <Button size="sm" variant="outline" data-testid={`track-${b.id}`} onClick={() => openDetails(b.id)} className="md:rounded-sm rounded-lg border-[#1C4532] text-[#1C4532] hover:bg-[#1C4532] hover:text-white text-xs h-9">
                            <Eye size={14} className="mr-1.5"/> {t("userDashboard.liveTrack")}
                          </Button>
                        )}
                        {b.status === "pending" && (
                          <Button variant="ghost" size="sm" data-testid={`cancel-${b.id}`} onClick={async () => {
                            await api.put(`/bookings/${b.id}/status`, { status: "cancelled" });
loadAll(); toast.success("Cancelled");
                           }} className="text-xs h-9">{t("userDashboard.cancel")}</Button>
                        )}
                        {b.status === "cancelled" && (
                          <Button size="sm" onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              const payload = { service_id: b.service_id, full_name: b.full_name, phone: b.phone, address: b.address, amount: b.amount, notes: b.notes, lat: b.lat, lng: b.lng };
                              await api.post("/bookings", payload);
                              toast.success("Booking re-submitted!");
                              loadAll();
                            } catch (err) { toast.error("Retry failed"); }
                          }} className="md:rounded-sm rounded-lg bg-[#D9734E] hover:bg-[#C26240] text-white text-xs h-9">
                            <RefreshCw size={14} className="mr-1.5"/> Retry
                          </Button>
                        )}
                        {b.status === "completed" && !b.rating && (
                          <Button size="sm" data-testid={`rate-${b.id}`} onClick={() => { setRateBooking(b); setRateOpen(true); }} className="md:rounded-sm rounded-lg bg-[#D9734E] hover:bg-[#C26240] text-white text-xs h-9">
                            <Star size={14} className="mr-2"/> {t("userDashboard.rate")}
                          </Button>
                        )}
                        {b.status === "completed" && b.rating && (
                          <span className="flex items-center gap-1 text-sm text-[#646964]">
                            <Star size={14} className="fill-[#D9734E] text-[#D9734E]"/>{b.rating.toFixed(1)}
                          </span>
                        )}
                      </div>
                    </div>
                  </article>
                ))}
              </div>
              {bookings.length === 0 && (
                <div className="text-center py-20 text-[#646964]">
                  <p className="text-sm">{t("userDashboard.noBookings")}</p>
                  <Button onClick={() => nav("/services")} data-testid="empty-book-cta" className="rounded-lg md:rounded-sm bg-[#1C4532] mt-4 text-white">{t("userDashboard.browseServices")} <ArrowRight size={14} className="ml-2"/></Button>
                </div>
              )}
            </TabsContent>

            <TabsContent value="nearby" className="mt-5 md:mt-6">
              <button onClick={() => setTab("profile")} className="flex items-center gap-1.5 text-sm text-[#646964] mb-3 md:hidden active:text-[#1A1D1A]">
                <ChevronLeft size={16} /> {t("userDashboard.backToProfile")}
              </button>
              <div className="grid grid-cols-12 gap-4 lg:gap-6">
                <div className="col-span-12 lg:col-span-7">
                  <Button onClick={detectLocation} disabled={loadingLoc} data-testid="detect-location" className="rounded-lg md:rounded-sm bg-[#1C4532] hover:bg-[#143324] text-white w-full md:w-auto">
                    {loadingLoc ? <><Loader2 size={14} className="animate-spin mr-2"/> {t("userDashboard.locating")}</> : <><MapPin size={14} className="mr-2"/> {t("userDashboard.useLiveLocation")}</>}
                  </Button>
                  <div className="mt-4 md:mt-5 rounded-xl md:rounded-sm overflow-hidden">
                    <MapView
                      center={coords ? [coords.lat, coords.lng] : [12.9716, 77.5946]}
                      userMarker={coords ? [coords.lat, coords.lng] : null}
                      workers={workers}
                      height={420}
                    />
                  </div>
                </div>
                <div className="col-span-12 lg:col-span-5 space-y-3">
                  <p className="text-xs uppercase tracking-wider text-[#646964] font-bold">{workers.length} {t("userDashboard.onlineNearby")}</p>
                  {workers.map(w => (
                    <div key={w.id} className="mobile-card haptic-tap p-4 md:rounded-sm rounded-xl flex justify-between items-center transition-all">
                      <div className="min-w-0 flex-1">
                        <p className="font-display text-base text-[#1A1D1A] truncate">{w.name}</p>
                        <p className="text-xs text-[#646964]">{services.find(s => s.id === w.service_id)?.name || "Service"} · {w.distance_km} km</p>
                      </div>
                      <span className="font-display text-lg text-[#1C4532] ml-3">₹{w.price}</span>
                    </div>
                  ))}
                  {coords && workers.length === 0 && <p className="text-sm text-[#646964] py-8 text-center">{t("userDashboard.noProsNearby")}</p>}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="profile" className="mt-0 -mx-4 sm:-mx-6 md:mt-6 md:mx-0 max-w-xl">
              <div className="md:hidden">
                <div className="flex flex-col items-center px-5 pt-8">
                  <div className="w-24 h-24 rounded-full border-[3px] border-white shadow-lg overflow-hidden bg-gradient-to-br from-[#B0B0B0] to-[#707070]">
                    <div className="w-full h-full flex items-center justify-center">
                      <User size={40} className="text-white/90" />
                    </div>
                  </div>
                  <h2 className="font-display text-xl font-semibold mt-4 text-[#1A1D1A]">{user.name}</h2>
                  <p className="text-sm text-[#646964]">{user.email}</p>

                  {!editing ? (
                    <button data-testid="profile-edit" onClick={() => setEditing(true)} className="mt-4 h-10 rounded-full bg-[#1C4532] hover:bg-[#143324] text-white text-sm font-medium px-8 active:scale-[0.97] transition-all">
                      {t("userDashboard.editProfile")}
                    </button>
                  ) : (
                    <div className="mt-4 w-full space-y-3">
                      <Input data-testid="profile-name" value={profile.name} onChange={e => setProfile({...profile, name: e.target.value})} placeholder={t("contact.form.name")} className="rounded-xl h-12 bg-white border-[#E2E0D9] text-sm" />
                      <Input data-testid="profile-phone" value={profile.phone} onChange={e => setProfile({...profile, phone: e.target.value})} placeholder={t("signup.form.phone")} className="rounded-xl h-12 bg-white border-[#E2E0D9] text-sm" />
                      <Input data-testid="profile-address" value={profile.address} onChange={e => setProfile({...profile, address: e.target.value})} placeholder={t("booking.form.address")} className="rounded-xl h-12 bg-white border-[#E2E0D9] text-sm" />
                      <div className="flex gap-3 pt-2">
                        <Button onClick={saveProfile} data-testid="profile-save" className="rounded-xl bg-[#1C4532] hover:bg-[#143324] text-white flex-1 h-11 text-sm">{t("userDashboard.saveChanges")}</Button>
                        <Button onClick={() => setEditing(false)} variant="outline" className="rounded-xl h-11 text-sm">{t("userDashboard.cancel")}</Button>
                      </div>
                    </div>
                  )}

                  {/* Menu sections */}
                  <div className="w-full mt-8 space-y-6 pb-8">
                    <ProfileSection title={t("userDashboard.section.account")}>
                      <ProfileRow icon={Calendar} label={t("userDashboard.myBookings")} onClick={() => setTab("bookings")} />
                      <ProfileRow icon={MapPin} label={t("userDashboard.nearbyPros")} onClick={() => setTab("nearby")} />
                    </ProfileSection>

                    <ProfileSection title={t("userDashboard.section.preferences")}>
                      <ProfileRow icon={Globe} label={t("userDashboard.language")} right={<div className="flex items-center gap-2"><span className="text-sm text-[#646964]">{t(`language.${i18n.language}`)}</span><ChevronRight size={16} className="text-[#C0C0C0]" /></div>} onClick={() => setLangOpen(true)} />
                    </ProfileSection>

                    <ProfileSection title={t("userDashboard.section.support")}>
                      <ProfileRow icon={HelpCircle} label={t("userDashboard.helpCenter")} />
                      <ProfileRow icon={LogOut} label={t("userDashboard.logOut")} onClick={() => setLogoutOpen(true)} />
                    </ProfileSection>
                  </div>
                </div>
              </div>

              {/* Desktop: keep original layout */}
              <div className="hidden md:block mobile-card p-8 md:rounded-sm rounded-xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <span className="w-12 h-12 grid place-items-center bg-[#1C4532] text-white rounded-sm"><User size={20}/></span>
                    <div className="min-w-0">
                      <p className="font-display text-lg md:text-xl truncate">{user.name}</p>
                      <p className="text-xs text-[#646964] font-mono truncate">{user.email}</p>
                    </div>
                  </div>
                  {!editing && <Button size="sm" variant="outline" data-testid="profile-edit" className="rounded-sm" onClick={() => setEditing(true)}>{t("userDashboard.edit")}</Button>}
                </div>
                <div className="mt-8 space-y-4">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-[#646964] mb-2 block">{t("contact.form.name")}</label>
                    <Input data-testid="profile-name" disabled={!editing} value={profile.name} onChange={e => setProfile({...profile, name: e.target.value})} className="rounded-sm h-10"/>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-[#646964] mb-2 block">{t("signup.form.phone")}</label>
                    <Input data-testid="profile-phone" disabled={!editing} value={profile.phone} onChange={e => setProfile({...profile, phone: e.target.value})} className="rounded-sm h-10"/>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-[#646964] mb-2 block">{t("booking.form.address")}</label>
                    <Input data-testid="profile-address" disabled={!editing} value={profile.address} onChange={e => setProfile({...profile, address: e.target.value})} className="rounded-sm h-10"/>
                  </div>
                  {editing && (
                    <div className="flex gap-3 pt-4">
                      <Button onClick={saveProfile} data-testid="profile-save" className="rounded-sm bg-[#1C4532] hover:bg-[#143324] text-white">{t("userDashboard.saveChanges")}</Button>
                      <Button onClick={() => setEditing(false)} variant="outline" className="rounded-sm">{t("userDashboard.cancel")}</Button>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>
      <RateBookingDialog booking={rateBooking} open={rateOpen} onOpenChange={setRateOpen} onSubmitted={loadAll}/>
      <BookingDetailsDialog bookingId={detailsId} open={detailsOpen} onOpenChange={setDetailsOpen}/>

      <LanguageDialog open={langOpen} onClose={() => setLangOpen(false)} />
      <AlertDialog open={logoutOpen} onOpenChange={setLogoutOpen}>
        <AlertDialogContent className="rounded-2xl max-w-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-lg text-[#1A1D1A]">{t("userDashboard.logoutTitle")}</AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-[#646964]">
              {t("userDashboard.logoutConfirm")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl h-11 text-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { logout(); nav("/"); }} className="rounded-xl h-11 text-sm bg-[#1C4532] hover:bg-[#143324] text-white">
              {t("userDashboard.logoutAction")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </>
  );
}

function ProfileSection({ title, children }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.2em] text-[#646964] font-bold px-1 mb-2">{title}</p>
      <div className="bg-white rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.04)] overflow-hidden divide-y divide-[#F3F1ED]">
        {children}
      </div>
    </div>
  );
}

function ProfileRow({ icon: Icon, label, onClick, right }) {
  return (
    <button onClick={onClick} className="w-full flex items-center gap-3.5 px-4 py-3.5 active:bg-[#FAF9F6] transition-colors text-left">
      <span className="w-8 h-8 grid place-items-center rounded-xl bg-[#FAF9F6] text-[#1A1D1A] shrink-0">
        <Icon size={17} />
      </span>
      <span className="flex-1 text-sm font-medium text-[#1A1D1A]">{label}</span>
      {right || <ChevronRight size={16} className="text-[#C0C0C0]" />}
    </button>
  );
}
